import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { LoadingService } from "@coso/loading";
import { convertMinutesToTime, convertTimeToMinutes, IS_SAAS } from "@coso/utility";
import { Observable, Subject } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";
import {IConfigurationHolder, IEmailProfileHolder} from "../../../models/auto-generated";
import { SettingsStore } from "../../../stores/settings.store";
import {EmailProfilesService, handleError} from "@coso-replicator/core";
import { ActivatedRoute } from "@angular/router";

@Component({
  standalone:false,
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"]
})
export class NotificationsComponent implements OnInit, OnDestroy, AfterViewInit {
  private unsubscribe = new Subject<void>();
  @ViewChild(NgForm) public formRef: NgForm;
  errorMessage: string;
  settings$: Observable<IConfigurationHolder>;
  readonly$: Observable<boolean>;
  notificationTypes: string[] = [];
  selectedProfileName: string = '';
  cumulativeLimit: number = 0;
  daysBeforeLicenseExpiry: number = 0;
  userPercentageLimit : number = 0;
  notificationProfileCount: number = 0;
  profiles: IEmailProfileHolder[] = [];
  instanceId: string;

  emailNotificationProfilesLink : string;

  constructor(public loadingService: LoadingService, 
    private settingsStore: SettingsStore,
    private emailProfilesService: EmailProfilesService,
    private route: ActivatedRoute,
    @Inject(IS_SAAS) public isSaaS: boolean
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngAfterViewInit(): void {
    // more info: https://angular.io/errors/NG0100
    setTimeout(() => {
      this.formRef.form.markAllAsTouched();
      this.formRef.form.statusChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.settingsStore.updateValidity("notifications", this.formRef.valid);
      });
    }, 0);
  }

  ngOnInit(): void {
    this.instanceId = this.route.parent.snapshot.paramMap.get("instance-id");
    this.emailNotificationProfilesLink = "/global-settings/email-notification-profiles";
    this.settings$ = this.settingsStore.settings$.pipe(
      filter((s) => !!s),
      tap((s) => {
        const cumulativeLimit = convertTimeToMinutes(s.notificationEmailCumulativeLimit);
        this.cumulativeLimit = cumulativeLimit < this.cumulativeLimit ? this.cumulativeLimit : cumulativeLimit;
        if(this.cumulativeLimit === 0) {
          this.cumulativeLimit = 15;
        }
        this.userPercentageLimit = s.notificationEmailMappedUserPercentageLimit === 0 ? 95 : s.notificationEmailMappedUserPercentageLimit;
        this.daysBeforeLicenseExpiry = s.notificationDaysBeforeLicenseExpiry === 0 ? 14 : s.notificationDaysBeforeLicenseExpiry;
        this.notificationTypes = s.notificationEmailEventSubscription?.split(",").sort() ?? [];
        this.selectedProfileName = s.notificationEmailProfileName;
      })
    );

    this.readonly$ = this.settingsStore.isReadOnly$;
    this.loadingService.start("Loading email profiles", "loadEmailProfiles");

    this.emailProfilesService.get().subscribe(
      (profiles) => {
        this.profiles = profiles;
        this.notificationProfileCount = profiles.length;
        this.loadingService.stop("loadEmailProfiles");
      },
      (error) => {
        this.errorMessage = `Failed to load email profiles due to: ${handleError(error)}`;
        this.loadingService.stop("loadEmailProfiles");
      }
    );
  }

  setNotificationType(current: IConfigurationHolder, type: string) {
    const index = this.notificationTypes.indexOf(type);
    if (index > -1) {
      this.notificationTypes.splice(index, 1);
    } else {
      this.notificationTypes.push(type);
    }
    this.notificationTypes.sort();
    this.updateSettings(current);
  }

  updateSettings(current: IConfigurationHolder) {
    current.notificationEmailCumulativeLimit = convertMinutesToTime(this.cumulativeLimit);
    current.notificationEmailEventSubscription = this.notificationTypes.join(",");
    current.notificationEmailMappedUserPercentageLimit = this.userPercentageLimit;
    current.notificationDaysBeforeLicenseExpiry = this.daysBeforeLicenseExpiry;
    this.settingsStore.updateSettings(current);
  }

  updateProfile(current: IConfigurationHolder, event: string) {
    current.notificationEmailProfileName = event;
    this.settingsStore.updateSettings(current);
  }
}
